import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { FaUserCircle } from 'react-icons/fa';
import { IoCloseSharp } from 'react-icons/io5';
import {Avatar as NextUIAvatar } from "@nextui-org/react";
import {Navbar as NextUINav, NavbarBrand, NavbarContent, NavbarItem, Button} from "@nextui-org/react";
import { Buffer } from 'buffer';


const backUrl = process.env.REACT_APP_BACK_URL;




// Styled components
const Navbar = styled(NextUINav)`
 padding: 0px;
`;

const Avatar = styled(NextUIAvatar)`
  width: 40px; /* Reduce size to fit better */
  height: 40px;
  cursor: pointer;
  border: 2px solid transparent;
  background-image: linear-gradient(white, white), 
                    linear-gradient(90deg, #1e90ff, #00cfff);
  background-origin: border-box;
  background-clip: content-box, border-box;
  box-shadow: 0px 4px 10px rgba(30, 144, 255, 0.3);
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-top: -13px;
  text-decoration: none;
  color: inherit;
  img {
    width: 150px;
    height: 65px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  justify-content: center;
  flex: 1;
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: absolute;
    top: 65px;
    right: 0;
    background-color: #ffffff;
    box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    z-index: 1;
    width: 100%;
    padding: 10px 0;
  }
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;  /* Ensures space between items */
  align-items: center;
  width: 100%; /* Takes full width */
  flex: 1;
  gap: 20px;  /* Add spacing between elements */

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;


const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;  /* Allow wrapping on smaller screens */
  justify-content: center;
  align-items: center;
  gap: 20px; /* Adds spacing between items */
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;


const NavItem = styled.li`
  margin: 0;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  font-family: Poppins;
  color: #0068C9;
  font-weight: 500;
  padding: 5px 10px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    color: #ffbf00;
  }

  &:hover::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 2px;
    background-color: #ffbf00;
    transition: width 0.3s ease;
  }

  &:focus {
    outline: none;
    color: #ffbf00;
    border-bottom: 2px solid #ffbf00;
  }
`;

const Hamburger = styled.div`
  display: none;
  cursor: pointer;
  @media (max-width: 768px) {
    display: block;
    margin-left: auto;
    padding: 10px;
    width: 30px;
  }
`;

const Bar = styled.div`
  width: 25px;
  height: 2px;
  background-color: #000080;
  margin: 4px 0;
  transition: 0.4s;
`;

// New container to add space between nav links and user icon
const RightSection = styled.div`
  display: flex;
  align-items: center;

  gap: 20px; /* Space between avatar and links */
  margin-right: 50px; /* Reduce margin to fit content properly */

  @media (max-width: 768px) {
    display: none;
  }
`;


const DropdownMenu = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 15px;
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 9999; /* 🚀 Force dropdown to be on top */
  margin-top: 10px;
`;



const DropdownItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  color: #000080;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const DropdownItemX = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  color: #000080;

  &:hover {
    background-color: #f0f0f0;
  }

  /* Hide on desktop (min-width: 768px) and show on mobile */
  @media (min-width: 768px) {
    display: none;
  }
`;

// Header Component
function Header({ isAuthenticated, handleLogout}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [userType, setUserType] = useState(null);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const storedUserType = localStorage.getItem('userType');
        setUserType(storedUserType);

        // Determine endpoint based on user type
        const endpoint =
          storedUserType === 'customer'
            ? `${backUrl}/customer/${userId}`
            : `${backUrl}/tradesman/${userId}`;

        console.log("Fetching profile picture from endpoint:", endpoint);
        
        const response = await axios.get(endpoint);
        console.log("Fetched user data:", response.data);

        // Check if profile picture exists
        if (response.data.profilePicture) {
          console.log("Profile picture found:", response.data.profilePicture);
        } else {
          console.warn("No profile picture found for this user.");
        }

        setProfilePicture(response.data.profilePicture);
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }
    };

    if (isAuthenticated) {
      fetchUserData();
    }
  }, [isAuthenticated]);
  
  

  const handleLogoutClick = () => {
    handleLogout();
    navigate('/'); // Redirect to home after logout
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDashboardClick = () => {
    const tradesmanId = localStorage.getItem('tradesmanId');
    if (userType === 'customer') {
      navigate('/customer-homepage');
    } else if (userType === 'tradesman' && tradesmanId) {
      navigate('/tradesman-homepage', { state: { tradesmanId } });
    } else {
      navigate('/login');
    }
  };

  // Close the dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <Navbar>
      <LogoLink to="/">
        <img src="/TUT.svg" alt="TradesUTrust Logo" />
      </LogoLink>

      <Hamburger onClick={toggleMenu}>
        {isOpen ? <IoCloseSharp size={30} color="#000080" /> : (
          <>
            <Bar />
            <Bar />
            <Bar />
          </>
        )}
      </Hamburger>
      <Navigation isOpen={isOpen}>
        <NavContainer>
        <NavList>
  {!isAuthenticated || userType !== 'tradesman' ? (
    <>
      <NavItem>
        <StyledLink 
          to="/post-job" 
          onClick={() => {
            setIsOpen(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Post a Job
        </StyledLink>
      </NavItem>
      <NavItem>
        <StyledLink 
          to="/how-it-works" 
          onClick={() => {
            setIsOpen(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          How to Find Good Trades
        </StyledLink>
      </NavItem>
      <NavItem>
        <StyledLink 
          to="/find-tradesman" 
          onClick={() => {
            setIsOpen(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Find a Trade
        </StyledLink>
      </NavItem>
      <NavItem>
        <StyledLink 
          to="/fast-signup" 
          onClick={() => {
            setIsOpen(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          Tradesperson Signup
        </StyledLink>
      </NavItem>
    </>
  ) : null}

  {/* Only show login inside hamburger menu on mobile */}
  {isAuthenticated ? (
    <>
      <DropdownItemX 
        onClick={() => {
          setIsOpen(false);
          handleDashboardClick();
        }}
      >
        Dashboard
      </DropdownItemX>
      <DropdownItemX 
        onClick={() => {
          setIsOpen(false);
          handleLogoutClick();
        }}
      >
        Logout
      </DropdownItemX>
    </>
  ) : (
    <NavItem>
      <StyledLink 
        to="/login" 
        onClick={() => {
          setIsOpen(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        Login
      </StyledLink>
    </NavItem>
  )}
</NavList>

        </NavContainer>
      </Navigation>

      {/* Show UserIcon and dropdown on desktop */}
      {isAuthenticated && (
        <RightSection>
          <Avatar
  src={
    profilePicture?.data?.data // If `profilePicture` has nested `data`
      ? `data:${profilePicture.contentType};base64,${Buffer.from(profilePicture.data.data).toString('base64')}`
      : profilePicture?.data?.$binary?.base64 // If using `$binary`
      ? `data:${profilePicture.contentType};base64,${profilePicture.data.$binary.base64}`
      : '/TUT.png' // Fallback to placeholder
  }
  alt="User Profile Picture"
  onError={(e) => {
    console.error("Error loading profile picture:", e);
    e.target.src = '/placeholder-image.png'; // Fallback image
  }}
  onClick={toggleDropdown}
/>

<DropdownMenu isOpen={isDropdownOpen} ref={dropdownRef}>
  <DropdownItem
    onClick={() => {
      handleDashboardClick(); // Navigate to Dashboard
      setIsDropdownOpen(false); // Close dropdown
    }}
  >
    Dashboard
  </DropdownItem>
  <DropdownItem
    onClick={() => {
      handleLogoutClick(); // Log out
      setIsDropdownOpen(false); // Close dropdown
    }}
  >
    Logout
  </DropdownItem>
</DropdownMenu>

        </RightSection>
      )}
    </Navbar>
  );
}

export default Header;
